import { Component, Inject, OnInit } from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { AirbnbHost2GuestReviewCategories, airbnbHost2GuestReviewCategoryTags } from '@tokeet-frontend/channels'
import {
  ActionFailed,
  DataCheckerService,
  Destroyable,
  htmlToPlainText,
  isSomething,
  Rental,
  SaveForm,
  selectRentalById,
  Toaster,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import * as lodash from 'lodash'
import { filter, finalize, map } from 'rxjs/operators'
import { select, Store } from '@ngrx/store'
import { selectReviewTemplates, Template } from '@tokeet-frontend/templates'
import { DataDictService } from '@tv3/services/data-dict.service'
import { TemplateGuard } from '@tv3/guards/template.guard'
import {
  ChannelReviewCategoryRating,
  getAIReviewCategoriesAvgRating,
  isReviewGuestExpired,
  ReviewService,
} from '@tokeet-frontend/reviews'
import { combineLatest, Observable } from 'rxjs'
import { FetchGuests } from '@tv3/store/guest/guest.actions'
import { Guest } from '@tv3/store/guest/guest.model'
import { selectGuestById } from '@tv3/store/guest/guest.selectors'

@Component({
  selector: 'app-review-airbnb-guest-dialog',
  templateUrl: './review-airbnb-guest-dialog.component.html',
  styleUrls: ['./review-airbnb-guest-dialog.component.scss'],
})
export class ReviewAirbnbGuestDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    category_ratings: this.fb.array([]),
    private_feedback: [''],
    public_review: ['', [Validators.required]],
    is_reviewee_recommended: [null, [Validators.required]],
  })

  templates$: Observable<Template[]>

  get categoryRatings() {
    return this.form.get('category_ratings') as FormArray
  }

  isEditable = true
  isExpired = false
  isGeneratingAIReview = false

  categories = Object.values(AirbnbHost2GuestReviewCategories)
  tags = lodash.groupBy(airbnbHost2GuestReviewCategoryTags, (t) => t.category)

  isLoading = true

  get inquiry() {
    return this.data.inquiry
  }

  guest: Guest
  rental: Rental

  constructor(
    private dataDictService: DataDictService,
    private store: Store<any>,
    public dialogRef: MatDialogRef<ReviewAirbnbGuestDialogComponent>,
    private fb: FormBuilder,
    private toaster: Toaster,
    private reviewService: ReviewService,
    private dataChecker: DataCheckerService,
    @Inject(MAT_DIALOG_DATA) public data: { inquiry: Inquiry }
  ) {
    super()
    this.dataChecker.check([TemplateGuard])
    this.store.dispatch(FetchGuests({ ids: [this.inquiry.guestId] }))
    this.templates$ = this.store.pipe(
      select(selectReviewTemplates(getAIReviewCategoriesAvgRating(this.inquiry.ratings)))
    )
  }

  ngOnInit(): void {
    combineLatest([
      this.store.pipe(select(selectGuestById, { id: this.inquiry.guestId })),
      this.store.pipe(select(selectRentalById(this.inquiry.rentalId))),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([guest, rental]) => {
        this.guest = guest
        this.rental = rental
      })

    this.reviewService
      .getBookingReviews(this.inquiry.id)
      .pipe(
        finalize(() => (this.isLoading = false)),
        map((data) => data.reviewToGuest),
        filter(isSomething)
      )
      .subscribe(
        (review) => {
          this.form.patchValue({
            category_ratings: review.categoryRatings,
            private_feedback: review.privateFeedback,
            public_review: review.text,
            is_reviewee_recommended: review.isRevieweeRecommended,
          })

          this.isExpired = isReviewGuestExpired(review)
          this.isEditable = !review.submitted && !this.isExpired
          if (review.submitted) {
            lodash.forEach(review.categoryRatings, (item) => {
              this.addCategoryRating(item)
            })
          } else if (this.isEditable) {
            const bookingRatingsByCategory = lodash.keyBy(this.inquiry.ratings, (t) => t.type)
            lodash.forEach(this.categories, (c) =>
              this.addCategoryRating({
                category: c,
                rating: bookingRatingsByCategory[c]?.stars || null,
                comment: bookingRatingsByCategory[c]?.comment,
                review_category_tags: bookingRatingsByCategory[c]?.tags || [],
              })
            )
          }
          if (!this.isEditable) {
            this.form.disable()
          }
        },
        (error) => {
          this.toaster.error($localize`You cannot review this guest now`, '', error)
          this.form.disable()
        }
      )
  }

  onPublicTemplateChange([template]: [Template]) {
    this.getTemplateMessage(template).subscribe((compiledMessage) => {
      this.form.patchValue({ public_review: compiledMessage })
    })
  }

  onPrivateTemplateChange([template]: [Template]) {
    this.getTemplateMessage(template).subscribe((compiledMessage) => {
      this.form.patchValue({ private_feedback: compiledMessage })
    })
  }

  private getTemplateMessage(template: Template) {
    const inquiry = this.inquiry
    return this.dataDictService
      .resolve(template.body, {
        inquiry: inquiry,
        rental: this.rental || inquiry.rental,
        guest: this.guest || inquiry.guest,
      })
      .pipe(map((m) => htmlToPlainText(m)))
  }

  onAIGuestReview() {
    this.isGeneratingAIReview = true
    const inquiry = this.inquiry
    const data = this.form.getRawValue()
    const categories = data.category_ratings.map((cr) => ({
      type: cr.category,
      stars: cr.rating,
      comment: cr.comment || '',
      tags: cr.review_category_tags || [],
    }))

    this.reviewService
      .aiGuestReview({ inquiryId: inquiry.id, account: inquiry.account, categories })
      .pipe(finalize(() => (this.isGeneratingAIReview = false)))
      .subscribe(
        (message) => {
          this.form.patchValue({ public_review: message })
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onRecommend(v: boolean) {
    this.form.patchValue({ is_reviewee_recommended: v })
  }

  onRate(form: FormGroup, v: number) {
    form.patchValue({ rating: v })
  }

  addCategoryRating(data?: Partial<ChannelReviewCategoryRating>) {
    const item = this.fb.group({
      category: [data?.category, [Validators.required]],
      rating: [data?.rating, [Validators.required]],
      comment: [data?.comment || '', []],
      review_category_tags: [data?.review_category_tags || []],
    })
    item.get('category').disable()
    this.categoryRatings.push(item)
  }

  removeCategoryRating(index: number) {
    this.categoryRatings.removeAt(index)
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const payload = this.form.getRawValue()
    this.isLoading = true
    this.reviewService
      .reviewAirbnbGuest(this.inquiry.inquiryId, payload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        () => {
          this.toaster.success($localize`Review saved successfully`)
          this.dialogRef.close()
        },
        (error) => {
          this.toaster.error($localize`Unable to save review`, '', error)
        }
      )
  }
}
